import type React from 'react';
import type { TFunction } from 'i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { OptionMarkedLabel } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';

export type GetMarkedOptionLabel<T = unknown> = (option: T) => React.ReactNode | string;

interface MarkedOptionLabelBaseProps<T> {
  t: TFunction;
  markedMarker?: string;
  inactiveMarker?: string;
  toOption?: (selectedItem: T) => Option<unknown>;
}

interface MarkedOptionLabelPositiveProps<T> extends MarkedOptionLabelBaseProps<T> {
  positive?: boolean;
  warning?: never;
}

interface MarkedOptionLabelWarningProps<T> extends MarkedOptionLabelBaseProps<T> {
  positive?: never;
  warning?: boolean;
}

type MarkedOptionLabelProps<T> = MarkedOptionLabelPositiveProps<T> | MarkedOptionLabelWarningProps<T>;

const isOption = (option: unknown): option is Option<unknown> =>
  typeof option === 'object' && option !== null && 'label' in option && 'value' in option;

/**
 * Custom hook to generate a marked option label based on the option's properties.
 *
 * @param {TFunction} t - The translation function from i18next.
 * @param markedMarker - The marker to use for marked options instead of default.
 * @param inactiveMarker - The marker to use for inactive options instead of default.
 * @param positive - Whether to mark the option as positive.
 * @param warning - Whether to mark the option as a warning.
 * @param toOption - A function to convert the selected item to Option type.
 * @returns {GetMarkedOptionLabel} - A function that returns a React node or string based on the option's properties.
 */
export function useMarkedOptionLabel<T>({
  t,
  positive,
  warning,
  markedMarker,
  inactiveMarker,
  toOption,
}: MarkedOptionLabelProps<T>): GetMarkedOptionLabel<T> {
  const markedMarkerValue = markedMarker ?? t('common:DEFAULT');

  return useCallbackRef((selectedItem) => {
    let option;
    if (!isOption(selectedItem)) {
      if (!toOption) throw new Error('Pass values must be Option. Pass "toOption" to convert to Option.');
      option = toOption(selectedItem);
    } else {
      option = selectedItem;
    }

    if (option.marked) {
      if (positive) {
        return <OptionMarkedLabel option={option} positive marker={markedMarkerValue} />;
      }
      if (warning) {
        return <OptionMarkedLabel option={option} warning marker={markedMarkerValue} />;
      }
      return <OptionMarkedLabel option={option} informative marker={markedMarkerValue} />;
    }
    if (typeof option.active === 'boolean' && !option.active) {
      return <OptionMarkedLabel option={option} negative marker={inactiveMarker ?? t('common:INACTIVE')} />;
    }
    return option.label;
  });
}
